import { callLambda, lambdaErrorCode } from './lambda.js';

// The recovery phrase check (issue #15). The server keeps a few of the phrase's letters, which the browser can't read:
// public-ledger/phrase-challenge hands out the positions of 4 of them, and public-ledger/phrase-verify checks the typed
// letters and is the only writer of ledgerCred.verifiedAt.

/** A letter's place in the phrase, both 1-based: the wordIndex-th word's letterIndex-th letter. */
export interface PhrasePosition {
  wordIndex: number;
  letterIndex: number;
}

export interface PhraseChallenge {
  challengeId: string;
  positions: PhrasePosition[];
  expiresAt: string;
}

export type PhraseVerifyResult = { verified: true; verifiedAt: string } | { verified: false; attemptsLeft: number };

// The only workflow whose Recover public ledger keys card saves the phrase's letters: public-ledger/phrase-save refuses
// any other with NOT_ACCOUNT_CREATION_CARD
export const ACCOUNT_CREATION_WORKFLOW = '@ns/create-public-ledger-account';

/**
 * Whether a Recover public ledger keys card in this workflow saves the phrase's letters. Only account creation's does, so
 * any later recovery sends just a check's 4 letters, never a fresh share of the phrase.
 */
export function savesPhraseLetters(workflowName: string | null | undefined): boolean {
  return workflowName === ACCOUNT_CREATION_WORKFLOW;
}

// The lambdas answer with their result object as `res`; a refusal arrives as a thrown LambdaError with the lambda's code
function answer<T>(result: { res: unknown }, fallback: string): T {
  if (!result.res || typeof result.res !== 'object') throw new Error(fallback);
  return result.res as T;
}

/** Starts a check for one of the caller's own profiles. Refusals arrive as LambdaErrors carrying the lambda's code. */
export async function requestPhraseChallenge(token: string | undefined, personId: string): Promise<PhraseChallenge> {
  const result = await callLambda('public-ledger/phrase-challenge', 'POST', token, JSON.stringify({ personId }));
  return answer<PhraseChallenge>(result, 'The recovery phrase check could not be started');
}

/** Sends the 4 letters, in the order of the challenge's positions. */
export async function verifyPhraseAnswers(
  token: string | undefined,
  challengeId: string,
  answers: string[],
): Promise<PhraseVerifyResult> {
  const result = await callLambda(
    'public-ledger/phrase-verify',
    'POST',
    token,
    JSON.stringify({ challengeId, answers }),
  );
  return answer<PhraseVerifyResult>(result, 'The recovery phrase check could not be completed');
}

/** The letters a challenge asks for, read from a whole phrase; an empty string where the phrase has no such letter. */
export function lettersFromPhrase(phrase: string, positions: PhrasePosition[]): string[] {
  const words = phrase.trim().split(/\s+/).filter(Boolean);
  return positions.map(({ wordIndex, letterIndex }) =>
    (words[wordIndex - 1] ?? '').charAt(letterIndex - 1).toLowerCase(),
  );
}

/**
 * Passes a check with the whole phrase, which the browser holds right after the account is made or recovered: the server
 * then records the phrase as checked (ledgerCred.verifiedAt).
 */
export async function verifyWithPhrase(
  token: string | undefined,
  personId: string,
  phrase: string,
): Promise<PhraseVerifyResult> {
  const challenge = await requestPhraseChallenge(token, personId);
  return verifyPhraseAnswers(token, challenge.challengeId, lettersFromPhrase(phrase, challenge.positions));
}

export interface PhraseLetter extends PhrasePosition {
  letter: string;
}

/**
 * About 20% of a phrase's letters at random, each with its 1-based word and letter position, no position twice, in
 * phrase order: what public-ledger/phrase-save keeps for later checks.
 */
export function extractRecoveryLetters(phrase: string, random: () => number = Math.random): PhraseLetter[] {
  const words = phrase.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const totalLetters = words.join('').length;
  if (totalLetters < 1) throw new Error('The recovery phrase is empty');

  const wanted = Math.floor(totalLetters * 0.2);
  const picked = new Map<string, PhraseLetter>();
  while (picked.size < wanted) {
    const wordIndex = Math.floor(random() * words.length);
    const word = words[wordIndex] ?? '';
    const letterIndex = Math.floor(random() * word.length);
    const key = `${wordIndex}:${letterIndex}`;
    if (!picked.has(key)) {
      picked.set(key, { letter: word.charAt(letterIndex), wordIndex: wordIndex + 1, letterIndex: letterIndex + 1 });
    }
  }
  return [...picked.values()].sort((a, b) => a.wordIndex - b.wordIndex || a.letterIndex - b.letterIndex);
}

/**
 * Saves the letters and the first verifiedAt from the caller's open Recover public ledger keys card. Refused with
 * PHRASE_LETTERS_SAVED once a person's letters are saved.
 */
export async function savePhraseLetters(
  token: string | undefined,
  jobId: string,
  letters: PhraseLetter[],
): Promise<{ saved: number; verifiedAt: string }> {
  const result = await callLambda('public-ledger/phrase-save', 'POST', token, JSON.stringify({ jobId, letters }));
  return answer<{ saved: number; verifiedAt: string }>(result, 'Your recovery phrase could not be saved');
}

/**
 * The cap's refusal (TOO_MANY_CHALLENGES) as a person reads it: the server sends the time the block ends as an ISO time,
 * shown here as "today" or "tomorrow" and a time of day in the person's own time zone. Null for any other message.
 */
export function capRefusalText(message: string, locale?: string, now: Date = new Date()): string | null {
  const iso = /Try again tomorrow at (\S+?)\.?$/.exec(message)?.[1];
  const until = iso ? new Date(iso) : null;
  if (!until || Number.isNaN(until.getTime())) return null;
  const day = until.toDateString() === now.toDateString() ? 'today' : 'tomorrow';
  const time = until.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  return `Too many recovery phrase checks today. Try again ${day} at ${time}.`;
}

/** What a person is told for a refused check: the cap's refusal with its time, else the lambda's own message. */
export function phraseCheckRefusal(err: unknown, fallback: string): string {
  const message = err instanceof Error && err.message ? err.message : '';
  if (lambdaErrorCode(err) === 'TOO_MANY_CHALLENGES') {
    return capRefusalText(message) ?? 'Too many recovery phrase checks today. Try again tomorrow.';
  }
  return message || fallback;
}
